import { getLogger } from '@logtape/logtape';

import { ApiError, api } from '@/lib/api-client.ts';

import {
    type AppointmentChainConfiguration,
    appointmentChainConfigurationSchema,
} from '@/schemas/admin/appointment-chain-configurations.schema.ts';

import { ServiceUrl } from '@/services/ServiceURL.ts';

import { serializeApiError } from '@/utils/log-utils.ts';

const logger = getLogger(['service', 'admin-chain-config']);

interface FetchChainConfigsOptions {
    showDeleted?: boolean;
    /** Booking flow only: asks the backend to enrich each step with `eligible_practitioners`. */
    includeEligiblePractitioners?: boolean;
}

/**
 * Service for appointment chain configuration related API calls.
 */
export const fetchChainConfigs = async (
    customerId: string,
    { showDeleted, includeEligiblePractitioners }: FetchChainConfigsOptions = {}
): Promise<AppointmentChainConfiguration[]> => {
    if (!customerId) {
        throw new ApiError(400, 'missing customer id');
    }

    const params: Record<string, boolean> = {};
    if (showDeleted) params.include_deleted = true;
    if (includeEligiblePractitioners) params.include_eligible_practitioners = true;

    const response = await api.get<{ data: unknown }>(
        ServiceUrl.PRACTITIONER_API_DOMAIN.SECURE_ADMIN.USER.APPOINTMENT_CHAIN_CONFIGS,
        {
            headers: { customer_id: customerId },
            params: Object.keys(params).length > 0 ? params : undefined,
        }
    );

    const rawItems = response.data?.data;
    if (!Array.isArray(rawItems)) {
        const typeLabel = rawItems === null ? 'null' : typeof rawItems;
        throw new ApiError(502, `Expected array in appointment chain configurations response, got: ${typeLabel}`);
    }

    const validItems = rawItems.flatMap((item, index) => {
        const parsed = appointmentChainConfigurationSchema.safeParse(item);
        if (parsed.success) return [parsed.data];
        logger.warn('Invalid appointment chain configuration at index {index}', {
            index: index,
            error: parsed.error,
            ...serializeApiError(parsed.error),
        });
        return [];
    });

    if (rawItems.length > 0 && validItems.length === 0) {
        throw new ApiError(502, 'All appointment chain configurations failed validation');
    }

    // Move deleted chains to the end; stable sort keeps each group's order.
    if (showDeleted) {
        validItems.sort((a, b) => Number(Boolean(a.meta.deleted_at)) - Number(Boolean(b.meta.deleted_at)));
    }

    return validItems;
};

export const deleteChainConfig = async (
    customerId: string,
    chainConfigId: string,
    instanceId: string
): Promise<void> => {
    if (!customerId) {
        throw new ApiError(400, 'missing customer id');
    }
    if (!chainConfigId) {
        throw new ApiError(400, 'missing chain config id');
    }
    if (!instanceId) {
        throw new ApiError(400, 'missing instance id');
    }
    await api.delete(
        `${ServiceUrl.PRACTITIONER_API_DOMAIN.SECURE_ADMIN.USER.APPOINTMENT_CHAIN_CONFIGS}/${chainConfigId}`,
        {
            headers: { customer_id: customerId },
            params: { instance_id: instanceId },
        }
    );
};
